import React, { createContext, useState, useContext, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../lib/api';

// Create payment context
const PaymentContext = createContext();

export function PaymentProvider({ children }) {
  const [orderDetails, setOrderDetails] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user, token } = useAuth();

  // Create a new order in Razorpay
  const createOrder = useCallback(
    async (subscriptionId, amount) => {
      if (!user || !token) {
        setError('You must be logged in to make a payment');
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await api.post(
          'payments/create-order',
          {
            subscription_id: subscriptionId,
            amount: amount,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const orderData = response.data;
        setOrderDetails(orderData);
        setPaymentStatus('created');
        return orderData;
      } catch (err) {
        setError(
          err.response?.data?.detail ||
            'Failed to create payment order. Please try again.'
        );
        return null;
      } finally {
        setLoading(false);
      }
    },
    [user, token]
  );

  // Initialize Razorpay payment
  const initiatePayment = useCallback(
    (orderData, userData) => {
      return new Promise((resolve, reject) => {
        if (!orderData || !orderData.razorpay_order_id) {
          setError('Invalid order data');
          reject('Invalid order data');
          return;
        }

        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: orderData.amount * 100, // Convert to paise
          currency: 'INR',
          name: 'Curators Homes',
          description: 'Flower Subscription',
          order_id: orderData.razorpay_order_id,
          handler: function (response) {
            // This function gets called after payment is successful
            resolve(response);
          },
          prefill: {
            name: userData?.name || user?.name || '',
            email: userData?.email || user?.email || '',
            contact: userData?.phone || user?.phone || '',
          },
          notes: {
            subscription_id: orderData.subscription_id,
          },
          theme: {
            color: '#7C4DFF',
          },
          modal: {
            ondismiss: function () {
              reject('Payment cancelled by user');
            },
          },
        };

        // Open Razorpay checkout
        try {
          const rzp = new window.Razorpay(options);
          rzp.open();
        } catch (err) {
          setError('Failed to initialize payment gateway. Please try again.');
          reject(err);
        }
      });
    },
    [user]
  );

  // Verify payment with backend after Razorpay success
  const verifyPayment = useCallback(
    async (paymentResponse) => {
      if (!token) {
        setError('Authentication required');
        return false;
      }

      setLoading(true);
      try {
        const response = await api.post(
          'payments/verify',
          {
            razorpay_order_id: paymentResponse.razorpay_order_id,
            razorpay_payment_id: paymentResponse.razorpay_payment_id,
            razorpay_signature: paymentResponse.razorpay_signature,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.success) {
          setPaymentStatus('paid');
          return true;
        } else {
          setError('Payment verification failed');
          setPaymentStatus('failed');
          return false;
        }
      } catch (err) {
        setError(
          err.response?.data?.detail ||
            'Payment verification failed. Please contact support.'
        );
        setPaymentStatus('failed');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  // Get payment status
  const getPaymentStatus = useCallback(
    async (paymentId) => {
      if (!token) {
        setError('Authentication required');
        return null;
      }

      try {
        const response = await api.get(`payments/status/${paymentId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setPaymentStatus(response.data.status);
        return response.data;
      } catch (err) {
        setError(
          err.response?.data?.detail ||
            'Failed to get payment status. Please try again.'
        );
        return null;
      }
    },
    [token]
  );

  // Get all payments for a subscription
  const getSubscriptionPayments = useCallback(
    async (subscriptionId) => {
      if (!token) {
        setError('Authentication required');
        return [];
      }

      try {
        const response = await api.get(
          `payments/subscription/${subscriptionId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        return response.data;
      } catch (err) {
        setError(
          err.response?.data?.detail ||
            'Failed to get payment history. Please try again.'
        );
        return [];
      }
    },
    [token]
  );

  // Reset payment state
  const resetPayment = useCallback(() => {
    setOrderDetails(null);
    setPaymentStatus('pending');
    setError(null);
  }, []);

  const contextValue = {
    orderDetails,
    paymentStatus,
    error,
    loading,
    createOrder,
    initiatePayment,
    verifyPayment,
    getPaymentStatus,
    getSubscriptionPayments,
    resetPayment,
  };

  return (
    <PaymentContext.Provider value={contextValue}>
      {children}
    </PaymentContext.Provider>
  );
}

// Custom hook to use the payment context
export function usePayment() {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
}

export default PaymentContext;
